import {useAppDispatch, useAppSelector} from "../redux/stateHooks";
import {QueryRatingsVariablesType} from "../definitions/types";
import {
    setRatingPagination,
    toggleRatingPaginationSentimentFilter,
    toggleRatingPaginationStarFilter,
    updateRatingPagination
} from "../redux/slices/ratingPagination";
import {RatingPaginationSortType, SentimentType} from "../definitions/enums";
import {useState} from "react";
import {useProductRatingPaginationFragment} from "./useProductRatingPaginationFragment";

export const useRatingPaginationVariables = () => {
    const dispatch = useAppDispatch();
    const variables = useAppSelector((state) => state.ratingPagination.value);
    const {model: {paginationModel: {pagination}}} = useProductRatingPaginationFragment();
    const [localSearchTerm, setLocalSearchTerm] = useState<string>('');

    return {
        variables: {
            ...variables,
            search: {
                term: localSearchTerm,
                searchedTerm: variables.searchTerm,
                hasTerm: !!variables.searchTerm,
                isDifferent: variables.searchTerm !== localSearchTerm,
                isLongEnough: localSearchTerm.length >= 2,
                isButtonDisabled: localSearchTerm.length < 2 || variables.searchTerm === localSearchTerm
            }
        },
        mutations: {
            setActiveIndex: (variables: QueryRatingsVariablesType) => dispatch(setRatingPagination(variables)),
            update: (variables: Partial<QueryRatingsVariablesType>) => dispatch(updateRatingPagination(variables)),
            setSortBy: (sortBy: RatingPaginationSortType | string) => {
                const value: RatingPaginationSortType = typeof sortBy === "string" ? sortBy as RatingPaginationSortType : sortBy;
                return dispatch(updateRatingPagination({sortBy: value}));
            },
            setSearchTerm: setLocalSearchTerm,
            search: () => dispatch(updateRatingPagination({searchTerm: localSearchTerm})),
            clearSearchTerm: () => {
                setLocalSearchTerm('');
                dispatch(updateRatingPagination({searchTerm: undefined}));
            },
            filters: {
                zeroFilters: !variables.searchTerm && variables.filters.grades.length === 0 && variables.filters.sentiments.length === 0,
                hasFilters: variables.searchTerm || variables.filters.grades.length > 0 || variables.filters.sentiments.length > 0,
                hasGradeFilters: variables.filters.grades.length > 0,
                hasSentimentFilters: variables.filters.sentiments.length > 0,
                gradeFilters: variables.filters.grades,
                sentimentFilters: variables.filters.sentiments,
                activeStar: (grade: 1 | 2 | 3 | 4 | 5) => variables.filters.grades.includes(grade),
                toggleStar: (grade: 1 | 2 | 3 | 4 | 5) => dispatch(toggleRatingPaginationStarFilter(grade)),
                activeSentiment: (sentiment: SentimentType) => variables.filters.sentiments.includes(sentiment),
                toggleSentiment: (sentiment: SentimentType) => dispatch(toggleRatingPaginationSentimentFilter(sentiment)),
                clear: () => {
                    setLocalSearchTerm('');
                    dispatch(updateRatingPagination({searchTerm: undefined, filters: {grades: [], sentiments: []}}));
                }
            }
        },
        pagination: {
            isDefault: pagination.isDefault,
            pageNumber: pagination.pageNumber,
            startItemNumber: pagination.startItemNumber,
            endItemNumber: pagination.endItemNumber,
            itemRange: pagination.itemRange,
            nextPageNumber: pagination.nextPageNumber,
            previousPageNumber: pagination.previousPageNumber,
            displayPageNumber: pagination.displayPageNumber,
            pageSize: pagination.pageSize,
            totalItemCount: pagination.totalItemCount,
            totalPageCount: pagination.totalPageCount,
            hasMorePages: pagination.hasMorePages,
            lastPageNumber: pagination.lastPageNumber,
            hasNextPage: pagination.hasNextPage,
            hasPreviousPage: pagination.hasPreviousPage,
            isFirstPage: pagination.isFirstPage,
            isLastPage: pagination.isLastPage
        }
    }
}